  import { useEffect, useState, useRef } from "react";
  import { Badge } from "./ui/badge";
  import { Timer } from "lucide-react";
  import { toast } from "sonner"; 
  import { Practice } from "@/domain/entities/Practice";
  import { bluetoothHelper } from "@/utils/bluetoothHelper";
  import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
  import { Button } from "./ui/button";
  import { PracticeRepository } from "../infrastructure/repositories/PracticeRepository";
  import { FolderRepository } from "../infrastructure/repositories/FolderRepository";
  import { SetList } from "./sets/SetList";
  import { PracticeClockDialog } from "./sets/PracticeClockDialog";
  import { DeleteSetDialog } from "./sets/DeleteSetDialog";
  import { CreatePracticeDialog } from "./sets/CreatePracticeDialog";
  import { PostRepository } from "@/infrastructure/repositories/PostRepository";
  import { AddPostDialog } from "./ui/AddPostDialog";
  import { Plus, TrendingUp, Heart, Share2, Save } from "lucide-react";
  import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
  import { secureStorage } from "@/utils/secureStorage";


  interface SetItem {
    id: string;
    number: number;
    reps: number;
    minutes: number;
    seconds: number;
    distance: number | "";
    energy_zone: string;
    description?: string;
  }
  interface PracticeLap {
  id: string | number;
  practice_id: string | number;
  rep: string | number;
  distance: string | number;
  interval_lap: string | number;
  energy_zone?: string;
  description?: string;
  createdAt?: string;
}


  interface SetsContentProps {
    laps?: any[];
    practiceId?: number | string;
    folderId?: number | string;
    name?: string;
    description?: string;
    practiceDate?: string;
    practiceTime?: string;
    courseTypeLookupId?: number | string;
      onClose?: () => void;
      onSectionChange?: (
    section: string,
    payload?: { sets?: any[]; practiceId?: number | string; name?: string , description?: string;}
  ) => void;
        sets?: any[];
    setSets?: React.Dispatch<React.SetStateAction<any[]>>;
  }

export function SetsContent({ 
  laps = [], 
  practiceId, 
  folderId,
  name, 
  description, 
  practiceDate,
  practiceTime,
  courseTypeLookupId,
  onClose,
  onSectionChange
}: SetsContentProps) {
    const [sets, setSets] = useState<SetItem[]>([]);
    const [noLaps, setNoLaps] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [setToDelete, setSetToDelete] = useState<SetItem | null>(null);
    const [loadingAction, setLoadingAction] = useState(false);
    const preferredEnergyZone = localStorage.getItem("preferred_energy_zone") || null; 
    const [isAddSetDialogOpen, setIsAddSetDialogOpen] = useState(false);

    const [singleRounds, setSingleRounds] = useState("1");
    const [singleReps, setSingleReps] = useState("1");
    const [singleDistance, setSingleDistance] = useState("25");
    const [singleMinutes, setSingleMinutes] = useState("0");
    const [singleSeconds, setSingleSeconds] = useState("30");
    const [singleEnergy, setSingleEnergy] = useState(preferredEnergyZone || "Warm Up");
    const [singleDescription, setSingleDescription] = useState("");
    const [isEmptySetsDialogOpen, setIsEmptySetsDialogOpen] = useState(false);
    const [singleFormError, setSingleFormError] = useState<string | null>(null);
    const [ladderFormError, setLadderFormError] = useState<string | null>(null);
    const [localPracticeId, setLocalPracticeId] = useState<number | string | undefined>(practiceId);
    const [isOpenPracticeDialogOpen, setIsOpenPracticeDialogOpen] = useState(false);
    const [allPractices, setAllPractices] = useState<Practice[]>([]);
    const [loadingPractices, setLoadingPractices] = useState(false);
    const [allPracticeLaps, setAllPracticeLaps] = useState<PracticeLap[]>([]);
    const [isDirtySets, setIsDirtySets] = useState(false);
    const setsLoadedRef = useRef(false);
    const isManuallyModifiedRef = useRef(false);

useEffect(() => {
  // If sets have been manually modified, don't reload from props/laps
  if (isManuallyModifiedRef.current) return;
  
  // Check if this is a draft practice
  const selectedPractice = JSON.parse(localStorage.getItem("selected_practice") || "{}");
  if (selectedPractice.isDraft && selectedPractice.sets && selectedPractice.sets.length > 0) {
    // Only load if we haven't loaded yet
    if (!setsLoadedRef.current) {
      const formatted = selectedPractice.sets.map((set: any, index: number) => ({
        id: set.id || `${Date.now()}-${index}`,
        number: index + 1,
        reps: set.reps || 1,
        minutes: set.minutes || 0,
        seconds: set.seconds || 0,
        distance: (set.distance !== undefined && set.distance !== null) ? set.distance : 25,
        energy_zone: set.energy_zone || "Warm Up",
        description: set.description || `Lap ${index + 1}`,
      }));
      setSets(normalizeSets(formatted));
      setIsDirtySets(true);
      setsLoadedRef.current = true;
    }
    return;
  }

  if (isDirtySets || setsLoadedRef.current) return;

  if (laps && laps.length > 0) {
    const sorted = [...laps].sort((a, b) => {
      if (a.createdAt && b.createdAt) {
        const diff =
          new Date(a.createdAt).getTime() -
          new Date(b.createdAt).getTime();
        if (diff !== 0) return diff;
      }
      return Number(a.id) - Number(b.id);
    });

    const formatted = sorted.map((lap, index) => {
      const parsedDistance = parseInt(String(lap.distance));
      return {
        id: lap.id?.toString() || `${Date.now()}-${index}`,
        number: index + 1,
        reps: parseInt(lap.rep) || 1,
        minutes: Math.floor(
          (parseInt(String(lap.interval_lap ?? (lap as any).interval ?? 0)) || 0) / 60
        ),
        seconds:
          (parseInt(String(lap.interval_lap ?? (lap as any).interval ?? 0)) || 0) % 60,
        distance: (!isNaN(parsedDistance) && lap.distance != null && lap.distance !== '') ? parsedDistance : 25,
        energy_zone: lap.energy_zone || "Warm Up",
        description: lap.description || `Lap ${index + 1}`,
      };
    });

    setSets(normalizeSets(formatted));
    setsLoadedRef.current = true;
  }
}, [laps, isDirtySets]);


useEffect(() => {
  setLocalPracticeId(practiceId);
  // Reset refs when practice changes
  setsLoadedRef.current = false;
  isManuallyModifiedRef.current = false;
}, [practiceId]);

useEffect(() => {
  setNoLaps(sets.length === 0);
}, [sets]);

  const energyZones = [
    "Warm Up",
    "En1 Moderate",
    "En3 Threshold",
    "Sp1 Best Average",
    "Sp2 Race Pace",
    "Sp3 Sprint",
    "Recovery",
    "Extra Rest",
    "Aerobic",
    "Endurance",
    "En2 Threshold"
  ];

  const normalizeSets = (list: SetItem[]) => {
    return list
      .sort((a, b) => a.number - b.number)
      .map((s, i) => ({ ...s, number: i + 1 }));
  };

  const ensureSetsExist = (): boolean => {
  if (!sets || sets.length === 0) {
    setIsEmptySetsDialogOpen(true);
    return false;
  }
  return true;
};


const [isLadderDialogOpen, setIsLadderDialogOpen] = useState(false);
const [ladderRounds, setLadderRounds] = useState("1");
const [ladderReps, setLadderReps] = useState("1");
const [ladderDistance, setLadderDistance] = useState("25");
const [ladderMinutes, setLadderMinutes] = useState("0");
const [ladderSeconds, setLadderSeconds] = useState("30");
const [ladderEnergy, setLadderEnergy] = useState(preferredEnergyZone || "Warm Up");
const [ladderDescription, setLadderDescription] = useState("");
const [isCreatingFromFavorite, setIsCreatingFromFavorite] = useState(false);

const toNonNegativeInt = (value: string) => {
  const n = Number(value);
  if (Number.isNaN(n)) return 0;
  return Math.max(0, Math.trunc(n));
};

const MAX_REPS = 1000;
const MAX_DISTANCE = 10000;

const validateReps = (value: string | number): number => {
  const num = typeof value === 'string' ? parseInt(value) || 0 : value;
  return Math.min(Math.max(0, num), MAX_REPS);
};

const validateDistance = (value: string | number): number => {
  const num = typeof value === 'string' ? parseInt(value) || 0 : value;
  return Math.min(Math.max(0, num), MAX_DISTANCE);
};

const hasNegativeValue = (...values: string[]) => {
  return values.some(v => {
    const n = Number(v);
    return !Number.isNaN(n) && n < 0;
  });
};

const loadAllPractices = async () => {
  try {
    setLoadingPractices(true);

    const repo = new PracticeRepository();
    const userId =
      localStorage.getItem("selected_swimmer_id") ||
      localStorage.getItem("current_user_swimmer_id");

    if (!userId) {
      toast.error("User not found");
      return;
    }

    const result = await repo.getAllPractices(userId);

    const favoritePractices = (result.practices || []).filter(
      (p) => p.isFavorite === true
    );

   const normalizedPractices = favoritePractices.map((p: Practice) => {
  (p as any).logicalPracticeId = p.practiceId;
  return p;
});


setAllPractices(normalizedPractices);

    setAllPracticeLaps(result.laps || []);
    setIsOpenPracticeDialogOpen(true);

  } catch (err) {
    console.error(err);
    toast.error("Failed to load practices");
  } finally {
    setLoadingPractices(false);
  }
};



const createLadderSets = () => {
  if (
    hasNegativeValue(
      ladderRounds,
      ladderReps,
      ladderDistance,
      ladderMinutes,
      ladderSeconds
    )
  ) {
    toast.error("Negative values are not allowed");
    return;
  }

  const rounds = toNonNegativeInt(ladderRounds);
  const reps = validateReps(ladderReps);
  const minutes = toNonNegativeInt(ladderMinutes);
  const seconds = toNonNegativeInt(ladderSeconds);
  const distance = validateDistance(ladderDistance);

  // Validate maximums
  if (reps !== toNonNegativeInt(ladderReps)) {
    toast.error(`Reps cannot exceed ${MAX_REPS}`);
    return;
  }
  if (distance !== toNonNegativeInt(ladderDistance)) {
    toast.error(`Distance cannot exceed ${MAX_DISTANCE} meters`);
    return;
  }

  const totalLadders = rounds * reps;
  if (totalLadders < 1) {
    toast.error("Rounds and reps must be at least 1");
    return;
  }

  const startingNumber = sets.length + 1;
  const totalSets: SetItem[] = [];

  for (let i = 0; i < totalLadders; i++) {
    totalSets.push({
      id: `${Date.now()}-${i}`,
      number: startingNumber + i,
      reps: 1,
      minutes,
      seconds,
      distance,
      energy_zone: ladderEnergy,
      description: ladderDescription || `Ladder ${i + 1}`,
    });
  }

  setSets(prev => {
    const updated = normalizeSets([...prev, ...totalSets]);
    isManuallyModifiedRef.current = true;
    
    // Update localStorage if this is a draft practice
    const selectedPractice = JSON.parse(localStorage.getItem("selected_practice") || "{}");
    if (selectedPractice.isDraft) {
      const updatedPractice = {
        ...selectedPractice,
        sets: updated,
      };
      localStorage.setItem("selected_practice", JSON.stringify(updatedPractice));
    }
    
    return updated;
  });
  //setNoLaps(false);
};



const createSingleSet = () => {
  if (
    hasNegativeValue(
      singleRounds,
      singleReps,
      singleDistance,
      singleMinutes,
      singleSeconds
    )
  ) {
    toast.error("Negative values are not allowed");
    return;
  }

  const rounds = toNonNegativeInt(singleRounds);
  const reps = validateReps(singleReps);
  const distance = validateDistance(singleDistance);

  if (rounds < 1) {
    toast.error("Rounds must be at least 1");
    return;
  }

  // Validate maximums
  if (reps !== toNonNegativeInt(singleReps)) {
    toast.error(`Reps cannot exceed ${MAX_REPS}`);
    return;
  }
  if (distance !== toNonNegativeInt(singleDistance)) {
    toast.error(`Distance cannot exceed ${MAX_DISTANCE} meters`);
    return;
  }

  setSets(prev => {
    const startingNumber = prev.length + 1;
    const createdSets: SetItem[] = [];

    for (let i = 0; i < rounds; i++) {
      createdSets.push({
        id: `${Date.now()}-${i}`,
        number: startingNumber + i,
        reps,
        minutes: toNonNegativeInt(singleMinutes),
        seconds: toNonNegativeInt(singleSeconds),
        distance,
        energy_zone: singleEnergy,
        description: singleDescription || `Set ${startingNumber + i}`,
      });
    }

    const updated = normalizeSets([...prev, ...createdSets]);
    isManuallyModifiedRef.current = true;
    
    // Update localStorage if this is a draft practice
    const selectedPractice = JSON.parse(localStorage.getItem("selected_practice") || "{}");
    if (selectedPractice.isDraft) {
      const updatedPractice = {
        ...selectedPractice,
        sets: updated,
      };
      localStorage.setItem("selected_practice", JSON.stringify(updatedPractice));
    }
    
    return updated;
  });
resetSingleSetForm();
  //setNoLaps(false);
};

 const resetSingleSetForm = () => {
  setSingleRounds("1");
  setSingleReps("1");
  setSingleDistance("25");
  setSingleMinutes("0");
  setSingleSeconds("30");
  setSingleEnergy(preferredEnergyZone || "Warm Up");
  setSingleDescription("");
  setSingleFormError(null);
};

const resetLadderForm = () => {
  setLadderRounds("1");
  setLadderReps("1");
  setLadderDistance("25");
  setLadderMinutes("0");
  setLadderSeconds("30");
  setLadderEnergy(preferredEnergyZone || "Warm Up");
  setLadderDescription("");
  setLadderFormError(null);
};


  const calculateTotalTime = () => {
    const totalSeconds = sets.reduce((total, set) => {
      const setTotalSeconds = set.reps * (set.minutes * 60 + set.seconds);
      return total + setTotalSeconds;
    }, 0);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const calculateTotalDistance = () => {
  return sets.reduce((sum, set) => {
    const distance =
      typeof set.distance === "number" ? set.distance : 0;

    const reps =
      typeof set.reps === "number" ? set.reps : 0;

    return sum + reps * distance;
  }, 0);
};


  const updateSet = (id: string, field: keyof SetItem, value: any) => {
    let validatedValue = value;
    
    // Apply maximum validation for reps and distance
    if (field === 'reps') {
      validatedValue = validateReps(value);
      if (validatedValue !== value) {
        toast.error(`Reps cannot exceed ${MAX_REPS}`);
      }
    } else if (field === 'distance') {
      validatedValue = validateDistance(value);
      if (validatedValue !== value) {
        toast.error(`Distance cannot exceed ${MAX_DISTANCE} meters`);
      }
    }
    
    setSets((prev) => {
      const updated = normalizeSets(
        prev.map((set) => (set.id === id ? { ...set, [field]: validatedValue } : set))
      );
      
      isManuallyModifiedRef.current = true;
      
      // Update localStorage if this is a draft practice
      const selectedPractice = JSON.parse(localStorage.getItem("selected_practice") || "{}");
      if (selectedPractice.isDraft) {
        const updatedPractice = {
          ...selectedPractice,
          sets: updated,
        };
        localStorage.setItem("selected_practice", JSON.stringify(updatedPractice));
      }
      
      return updated;
    });
  };


  const [isNewPracticeDialogOpen, setIsNewPracticeDialogOpen] = useState(false);
  const [newPracticeName, setNewPracticeName] = useState("");
  const [newPracticeDescription, setNewPracticeDescription] = useState("");
  const [isFavorite, setIsFavorite] = useState(false);
  const [pendingFavorite, setPendingFavorite] = useState<boolean | null>(null);
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false)
  const logSetSave = (...args: any[]) => {
    console.log("[SetsSaveDebug]", ...args);
  };

  console.log(pendingFavorite);


  useEffect(() => {
    const selected = JSON.parse(localStorage.getItem("selected_practice") || "{}");
    if (selected && selected.isFavorite !== undefined) {
      setIsFavorite(!!selected.isFavorite);
    } else {
      setIsFavorite(false); 
    }
  }, [practiceId]);

const withTimeout = async <T,>(
  promise: Promise<T>,
  timeoutMs = 30000,
  timeoutMessage = "Request timed out. Please try again."
): Promise<T> => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      promise,
      new Promise<never>((_, reject) => {
        timeoutId = setTimeout(() => reject(new Error(timeoutMessage)), timeoutMs);
      }),
    ]);
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
};

const resolveFolderId = (): string | null => {
  const propFolder = folderId == null ? "" : String(folderId).trim();
  if (
    propFolder &&
    propFolder !== "0" &&
    propFolder.toLowerCase() !== "null" &&
    propFolder.toLowerCase() !== "undefined"
  ) {
    return propFolder;
  }

  const selectedPractice =
    JSON.parse(localStorage.getItem("selected_practice") || "{}");
  const rawFolderId =
    selectedPractice.folder_id ??
    selectedPractice.folderId ??
    null;

  const normalizedFolderId = rawFolderId == null ? "" : String(rawFolderId).trim();
  if (
    normalizedFolderId &&
    normalizedFolderId !== "0" &&
    normalizedFolderId.toLowerCase() !== "null" &&
    normalizedFolderId.toLowerCase() !== "undefined"
  ) {
    return normalizedFolderId;
  }

  return null;
};

const handleSaveLaps = async () => {
  if (loadingAction) return;
  if (!ensureSetsExist()) return;

  try {
    setLoadingAction(true);
    logSetSave("handleSaveLaps:start", {
      localPracticeId,
      propFolderId: folderId,
      setsCount: sets.length,
      isFromFavoriteTemplate,
      name,
      description,
    });

    // New practice or favorite template → open dialog
    if (!localPracticeId || isFromFavoriteTemplate) {
      logSetSave("handleSaveLaps:new-practice-dialog", {
        reason: !localPracticeId ? "missing_localPracticeId" : "favorite_template",
      });
      setIsNewPracticeDialogOpen(true);
      return;
    }

    const resolvedFolderId = resolveFolderId();
    logSetSave("handleSaveLaps:resolvedFolderId", { resolvedFolderId });

    await withTimeout(
      savePracticeAndLaps(
        localPracticeId,
        name,
        description,
        undefined,
        resolvedFolderId
      ),
      30000,
      "Save is taking too long. Please try again."
    );

    setIsDirtySets(false);
    logSetSave("handleSaveLaps:success");
    onSectionChange?.("practice");
    onClose?.();

  } catch (error) {
    logSetSave("handleSaveLaps:error", error);
    toast.error("Failed to save laps. Please try again.");
  } finally {
    setLoadingAction(false);
    logSetSave("handleSaveLaps:end");
  }
};


const savePracticeAndLaps = async (
  practiceId: number | string,
  name?: string,
  description?: string,
  favorite?: boolean,
  folderId?: string | null
): Promise<void> => {
  const repo = new PracticeRepository();
  const folderRepo = new FolderRepository();

  const selectedSwimmerId = localStorage.getItem("selected_swimmer_id");
  let coachId: number;
  let swimmerId: number;

  if (selectedSwimmerId) {
    coachId = 0;
    swimmerId = Number(selectedSwimmerId);
  } else {
    coachId = Number(localStorage.getItem("coach_id")) || 0;
    swimmerId = Number(localStorage.getItem("current_user_swimmer_id")) || 0;
  }

  const now = new Date();
  const nowISO = now.toISOString();
  logSetSave("savePracticeAndLaps:start", {
    incomingPracticeId: practiceId,
    incomingFolderId: folderId,
    swimmerId,
    coachId,
    favorite,
    name,
    description,
    setsCount: sets.length,
  });

  const formattedLaps = sets.map((s) => ({
    id: 0,
    practice_lap_id: 0,
    practice_id: "",
    rep: s.reps || 1,
    distance: Number(s.distance) || 0,
    interval: (s.minutes || 0) * 60 + (s.seconds || 0),
    energy_zone: s.energy_zone || "Warm Up",
    description: s.description || "",
    time: 0,
    createdAt: nowISO,
    updated_at: nowISO,
    passedRepNumber: 0,
    isNoEditable: false,
    isPassed: 0,
    user_id: swimmerId.toString(),
    coach_id: coachId.toString(),
  }));

  const selectedPractice = JSON.parse(localStorage.getItem("selected_practice") || "{}");
  const parsePracticeDateForSave = (input: unknown): Date | null => {
    if (!input) return null;
    const raw = String(input).trim();
    if (!raw) return null;

    // Keep calendar day stable for YYYY-MM-DD values across timezones.
    const ymdMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (ymdMatch) {
      const year = Number(ymdMatch[1]);
      const month = Number(ymdMatch[2]);
      const day = Number(ymdMatch[3]);
      return new Date(year, month - 1, day, 12, 0, 0);
    }

    // MM/DD/YYYY from UI lists
    const mdyMatch = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (mdyMatch) {
      const month = Number(mdyMatch[1]);
      const day = Number(mdyMatch[2]);
      const year = Number(mdyMatch[3]);
      return new Date(year, month - 1, day, 12, 0, 0);
    }

    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const selectedPracticeDateRaw =
    selectedPractice.practice_date ??
    selectedPractice.PracticeDate ??
    selectedPractice.date ??
    practiceDate ??
    null;
  const preservedPracticeDate = parsePracticeDateForSave(selectedPracticeDateRaw) ?? now;
  const preservedPracticeTime =
    (selectedPractice.practice_time || selectedPractice.practiceTime || practiceTime || "AM").toString();
  const preservedCourseTypeLookupId =
    Number(
      selectedPractice.courseTypeLookupId ??
      selectedPractice.CourseTypeLookupId ??
      courseTypeLookupId ??
      1
    ) || 1;
  logSetSave("savePracticeAndLaps:selectedPractice", {
    id: selectedPractice?.id,
    practice_id: selectedPractice?.practice_id,
    practiceId: selectedPractice?.practiceId,
    folder_id: selectedPractice?.folder_id,
    folderId: selectedPractice?.folderId,
    folder_practice_model_id: selectedPractice?.folder_practice_model_id,
    folder_practice_id: selectedPractice?.folder_practice_id,
    isDraft: selectedPractice?.isDraft,
    preservedPracticeDateRaw: selectedPracticeDateRaw,
    preservedPracticeDateISO: preservedPracticeDate.toISOString(),
    preservedPracticeTime,
    preservedCourseTypeLookupId,
  });
  const selectedFolderId =
    selectedPractice.folder_id ?? selectedPractice.folderId ?? null;
  const normalizedSelectedFolderId =
    selectedFolderId == null ? "" : String(selectedFolderId).trim();
  let effectiveFolderId =
    folderId && String(folderId).trim()
      ? String(folderId).trim()
      : normalizedSelectedFolderId && normalizedSelectedFolderId !== "0"
      ? normalizedSelectedFolderId
      : null;

  const isFolderPracticeContext = Boolean(
    effectiveFolderId ||
      selectedPractice.isFolderPractice === true ||
      ((selectedPractice.folder_practice_model_id || selectedPractice.folder_practice_id) &&
        (selectedPractice.folder_id || selectedPractice.folderId))
  );
  logSetSave("savePracticeAndLaps:resolvedContext", {
    normalizedSelectedFolderId,
    effectiveFolderId,
    isFolderPracticeContext,
  });

  // =====================================================
  // =============== FOLDER PRACTICE FLOW ================
  // =====================================================
  if (isFolderPracticeContext) {
    // Keep practice inside the SAME folder and update in place when possible.
    // Reuse existing folder_practice_model_id to avoid creating duplicates.
    let targetPracticeId =
      String(
        selectedPractice.folder_practice_model_id ??
          selectedPractice.practiceId ??
          selectedPractice.practice_id ??
          practiceId ??
          ""
      ).trim() || (Math.floor(Math.random() * 90000) + 10000).toString();
    logSetSave("folderFlow:initialTargetPracticeId", { targetPracticeId });

    if (!effectiveFolderId) {
      const userIdForFolderLookup = swimmerId || coachId;
      if (userIdForFolderLookup) {
        try {
          logSetSave("folderFlow:lookupFolderMapping:start", { userIdForFolderLookup });
          const folderData = await withTimeout(
            folderRepo.getAllFoldersPaginated(userIdForFolderLookup, 1, 500),
            30000,
            "Fetching folder data timed out."
          );

          const candidateIds = new Set(
            [
              String(practiceId || "").trim(),
              String(selectedPractice.folder_practice_model_id || "").trim(),
              String(selectedPractice.folder_practice_id || "").trim(),
              String(selectedPractice.practiceId || "").trim(),
              String(selectedPractice.practice_id || "").trim(),
              String(selectedPractice.id || "").trim(),
            ].filter(Boolean)
          );

          const matchedPractice = (folderData?.practices || []).find((p: any) => {
            const modelId = String(p?.folder_practice_model_id ?? "").trim();
            const practiceIdValue = String(p?.folder_practice_id ?? "").trim();
            return candidateIds.has(modelId) || candidateIds.has(practiceIdValue);
          });
          logSetSave("folderFlow:lookupFolderMapping:result", {
            candidateIds: Array.from(candidateIds),
            matched: matchedPractice
              ? {
                  folder_id: matchedPractice.folder_id,
                  folder_practice_model_id: matchedPractice.folder_practice_model_id,
                  folder_practice_id: matchedPractice.folder_practice_id,
                }
              : null,
          });

          if (matchedPractice) {
            const matchedFolderId = String(matchedPractice.folder_id ?? "").trim();
            const matchedModelId = String(
              matchedPractice.folder_practice_model_id ?? ""
            ).trim();

            if (matchedFolderId && matchedFolderId !== "0") {
              effectiveFolderId = matchedFolderId;
            }
            if (matchedModelId) {
              targetPracticeId = matchedModelId;
            }
          }
        } catch (lookupErr) {
          console.warn("Failed to resolve folder mapping for practice:", lookupErr);
          logSetSave("folderFlow:lookupFolderMapping:error", lookupErr);
        }
      }
    }

    const targetFolderId = effectiveFolderId;
    logSetSave("folderFlow:resolvedTargets", {
      targetFolderId,
      targetPracticeId,
    });

    if (!targetFolderId) {
      logSetSave("folderFlow:missingFolderId:error", {
        selectedPractice,
        incomingFolderId: folderId,
      });
      throw new Error("Folder practice save failed: missing folder_id.");
    }

    // Folder flow follows replace semantics used elsewhere:
    // remove existing folder practice model, then recreate with updated laps.
    try {
      logSetSave("folderFlow:deleteFolderPractice:start", { targetPracticeId });
      await withTimeout(
        folderRepo.deleteFolderPractice(targetPracticeId),
        20000,
        "Deleting old folder practice timed out."
      );
      logSetSave("folderFlow:deleteFolderPractice:success", { targetPracticeId });
    } catch (err) {
      // New folder practice may not exist yet; continue with create.
      console.warn("Folder practice delete skipped/failed:", err);
      logSetSave("folderFlow:deleteFolderPractice:error", err);
    }

    formattedLaps.forEach((lap) => {
      lap.practice_id = targetPracticeId;
    });

    const practiceModel = Practice.create({
      id: targetPracticeId,
      practiceId: targetPracticeId,
      coachId,
      swimmerId,
      name: name || "Untitled Practice",
      description: description || "",
      date: preservedPracticeDate,
      practice_time: preservedPracticeTime,
      courseTypeLookupId: preservedCourseTypeLookupId,
      isFavorite: favorite ?? false,
      isFinished: false,
      imageData: "",
      folderId: targetFolderId,
    });
    logSetSave("folderFlow:storeFolderPractice:request", {
      endpoint: "folderpractice/manage_folder_practice_model",
      targetPracticeId,
      targetFolderId,
      lapsCount: formattedLaps.length,
      practiceName: practiceModel.name,
    });

    const response = await withTimeout(
      repo.storeFolderPractice(
        coachId,
        swimmerId,
        practiceModel,
        formattedLaps
      ),
      30000,
      "Saving practice timed out."
    );
    logSetSave("folderFlow:storeFolderPractice:response", {
      responseId: (response as any)?.id,
      responsePracticeId: (response as any)?.practiceId,
      responseFolderId: (response as any)?.folderId ?? (response as any)?.folder_id,
    });

    localStorage.setItem(
      "selected_practice",
      JSON.stringify({
        ...response,
        folder_id: targetFolderId,
        folder_practice_model_id: targetPracticeId,
        isFolderPractice: true,
        isFavorite: favorite ?? false,
      })
    );

    toast.success("Folder practice saved");
    logSetSave("folderFlow:success");
    return;
  }

  // =====================================================
  // =============== NORMAL PRACTICE FLOW =================
  // =====================================================
  const oldPracticeId = String(practiceId);
  const newPracticeId = (Math.floor(Math.random() * 90000) + 10000).toString();

  formattedLaps.forEach((lap) => {
    lap.practice_id = newPracticeId;
  });

  const practiceModel = Practice.create({
    id: newPracticeId,
    practiceId: newPracticeId,
    coachId,
    swimmerId,
    name: name || "Untitled Practice",
    description: description || "",
    date: preservedPracticeDate,
    practice_time: preservedPracticeTime,
    courseTypeLookupId: preservedCourseTypeLookupId,
    isFavorite: favorite ?? false,
    isFinished: false,
    imageData: "",
  });
  logSetSave("normalFlow:savePractice:request", {
    endpoint: "practice/manage_coach_practice",
    newPracticeId,
    lapsCount: formattedLaps.length,
    practiceName: practiceModel.name,
  });

  const response = await withTimeout(
    repo.save(
      coachId,
      swimmerId,
      practiceModel,
      formattedLaps
    ),
    30000,
    "Saving practice timed out."
  );
  logSetSave("normalFlow:savePractice:response", {
    responseId: (response as any)?.id,
    responsePracticeId: (response as any)?.practiceId,
  });

  const savedPracticeId = String(
    (response as any)?.practiceId ??
    (response as any)?.practice_id ??
    (response as any)?.id ??
    newPracticeId
  );

  if (savedPracticeId !== oldPracticeId) {
    try {
      logSetSave("normalFlow:deleteOldPractice:start", {
        oldPracticeId,
        savedPracticeId,
      });
      await withTimeout(
        repo.delete(oldPracticeId, swimmerId),
        20000,
        "Deleting old practice timed out."
      );
      logSetSave("normalFlow:deleteOldPractice:success", { oldPracticeId });
    } catch (err) {
      logSetSave("normalFlow:deleteOldPractice:error", err);
      throw err;
    }
  }

  localStorage.setItem(
    "selected_practice",
    JSON.stringify({
      ...response,
      isFolderPractice: false,
      isFavorite: favorite ?? false,
    })
  );

  toast.success("Practice saved");
  logSetSave("normalFlow:success");
};



  const handleConfirmDelete = (set: SetItem) => {
    setSetToDelete(set);
    setIsDeleteDialogOpen(true);
  };

  const removeSet = async () => {
    if (!setToDelete) return;

    try {

        // Inside removeSet
        if (!localPracticeId) {

          // Just remove locally
          setSets((prev) => {
            const updated = normalizeSets(prev.filter((s) => s.id !== setToDelete.id));
            isManuallyModifiedRef.current = true;
            
            // Update localStorage if this is a draft practice
            const selectedPractice = JSON.parse(localStorage.getItem("selected_practice") || "{}");
            if (selectedPractice.isDraft) {
              const updatedPractice = {
                ...selectedPractice,
                sets: updated,
              };
              localStorage.setItem("selected_practice", JSON.stringify(updatedPractice));
            }
            
            return updated;
          });
          setIsDeleteDialogOpen(false);
          setSetToDelete(null);
          return;
        }

      const repo = new PracticeRepository();
      const swimmerId =
        parseInt(localStorage.getItem("selected_swimmer_id") || "0") ||
        parseInt(localStorage.getItem("current_user_swimmer_id") || "0");

      const response = await repo.removePracticeLap(
        Number(practiceId),
        Number(setToDelete.id),
        swimmerId
      );

      if (response.status === 1) {
        setSets((prev) => {
          const updated = normalizeSets(prev.filter((s) => s.id !== setToDelete.id));
          isManuallyModifiedRef.current = true;
          
          // Update localStorage if this is a draft practice
          const selectedPractice = JSON.parse(localStorage.getItem("selected_practice") || "{}");
          if (selectedPractice.isDraft) {
            const updatedPractice = {
              ...selectedPractice,
              sets: updated,
            };
            localStorage.setItem("selected_practice", JSON.stringify(updatedPractice));
          }
          
          return updated;
        });
        if (sets.length <= 1) 
          setNoLaps(true);
      }    
      else {
        console.error(response.msg || "Failed to remove lap");
      }
    } catch (error) {
      console.error("Error removing lap:", error);
    } finally {
      setIsDeleteDialogOpen(false);
      setSetToDelete(null);
    }
  };

  // Power state - reserved for future power toggle functionality
  // const [isPowerOn, setIsPowerOn] = useState(false);
  const [isConnected, setIsConnected] = useState(bluetoothHelper.isConnected());
  const [isConnectDialogOpen, setIsConnectDialogOpen] = useState(false);

  // Note: handlePowerOnOff is available for future use with power toggle functionality
  // const handlePowerOnOff = async () => {
  //   try {
  //     if (!bluetoothHelper.isConnected()) {
  //       setIsConnectDialogOpen(true);
  //       return;
  //     }
  //     const newPowerState = !isPowerOn;
  //     setIsPowerOn(newPowerState);
  //     if (newPowerState) {
  //       await bluetoothHelper.sendString("PACE");
  //       toast.success("Power ON — Clock started & synced");
  //     } else {
  //       await bluetoothHelper.sendString("OFF");
  //       toast.warning("Power OFF — Clock stopped");
  //     }
  //   } catch (err) {
  //     console.error("Power toggle failed:", err);
  //     toast.error("Failed to toggle power");
  //   }
  // };

  // Clock View state

  const [isClockViewOpen, setIsClockViewOpen] = useState(false);
  const [timer, setTimer] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isRunning) {
      interval = setInterval(() => setTimer((prev) => prev + 1), 1000);
    }
    return () => clearInterval(interval);
  }, [isRunning]);

  const handleStartPractice = async () => {
    try {
      if (!isConnected) {
        const result = await bluetoothHelper.connect();
        setIsConnected(true);
        toast.success(`Connected to ${result.name}`);
      }

      setIsStarting(true);

      setTimer(0);
      await bluetoothHelper.sendString("RESET");

      await new Promise((resolve) => setTimeout(resolve, 200));

      // Start both together
      await bluetoothHelper.sendString("START");
      setIsRunning(true);
      setIsPaused(false);
      toast.success("Practice started — synced to device");

    } catch (error: any) {
      console.error("Failed to start practice:", error);
      toast.error("Unable to start practice");
    } finally {
      setIsStarting(false);
    }
  };

  const handlePausePractice = async () => {
    try {
      await bluetoothHelper.sendString("PAUSE");
      setIsRunning(false);
      setIsPaused(true);
      toast.warning("Practice paused — synced to device");
    } catch (error: any) {
      console.error("Pause failed:", error);
      toast.error("Failed to pause practice");
    }
  };

  const handleResumePractice = async () => {
    try {
      await bluetoothHelper.sendString("RESUME");
      setIsRunning(true);
      setIsPaused(false);
      toast.success("Resumed — synced to device");
    } catch (error: any) {
      console.error("Resume failed:", error);
      toast.error("Failed to resume practice");
    }
  };

  const handleResetPractice = async () => {
    try {
      await bluetoothHelper.sendString("RESET");
      setTimer(0);
      setIsRunning(false);
      setIsPaused(false);
      toast.info("Timer reset — synced to device");
    } catch (error: any) {
      console.error("Reset failed:", error);
      toast.error("Failed to reset timer");
    }
  };

  const handleFinishPractice = async () => {
    try {
      await bluetoothHelper.sendString("STOP");
      setIsRunning(false);
      setIsPaused(false);
      setTimer(0);
      toast.success("Practice finished — synced to device");
      setIsClockViewOpen(false);
    } catch (error: any) {
      console.error("Finish failed:", error);
      toast.error("Failed to finish practice");
    }
  };

  useEffect(() => {
    const handleClockConnection = async () => {
      if (isClockViewOpen) {
        try {
          if (!isConnected) {
            const result = await bluetoothHelper.connect();
            toast.success(`Connected to ${result.name}`);
            setIsConnected(true);

            // Reset device clock when connected
            await bluetoothHelper.sendString("RESET");
            console.log("Clock ready and reset");
          }
        } catch (err) {
          console.error("Failed to connect clock:", err);
          toast.error("Failed to connect to device");
        }
      } else {
        // Clock closed, disconnect
        if (isConnected) {
          await bluetoothHelper.sendString("OFF");
          setIsConnected(false);
          setIsRunning(false);
          setTimer(0);
          // setIsPowerOn(false); // Reserved for future power toggle functionality
          toast.info("Clock disconnected");
          console.log("Clock disconnected");
        }
      }
    };

    handleClockConnection();
  }, [isClockViewOpen]);

  const generatePracticePDF = async () => {
    const element = document.getElementById("practice-print-area");
    if (!element) return null;

    const html = `
      <html>
        <head>
          <title>Practice PDF</title>
        </head>
        <body>${element.innerHTML}</body>
      </html>
    `;

    const blob = new Blob([html], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    return url;
  };

  const [isAddPostDialogOpen, setIsAddPostDialogOpen] = useState(false);

  const handleSubmitPracticeFeedPost = async ({ text, mediaFile }: any) => {
    try {
      setLoadingAction(true);

      const postRepo = new PostRepository();

      let postPhoto = "";
      let postVideo = "";

      if (mediaFile) {
        if (mediaFile.type.startsWith("image/")) {
          const imgResp = await postRepo.uploadFeedImage(mediaFile);
          if (imgResp.status === 1) postPhoto = imgResp.image_link || "";
        } else if (mediaFile.type.startsWith("video/")) {
          const vidResp = await postRepo.uploadFeedVideo(mediaFile);
          if (vidResp.status === 1) postVideo = vidResp.video_link || "";
        }
      }

      const userId =
        Number(localStorage.getItem("current_user_swimmer_id")) ||
        Number(localStorage.getItem("selected_swimmer_id")) ||
        0;

      const coachUserId = Number(localStorage.getItem("coach_id")) || 0;

      const practiceImageData = "";

      const practiceData = {
        practice_id: String(practiceId || 0),
        practice_date: new Date().toISOString().slice(0, 19).replace("T", " "),
        practice_time: "AM",
        CourseTypeLookupId: 1,
        createdAt: new Date().toISOString().slice(0, 19).replace("T", " "),
      };

      const lapsData = sets.map((s) => ({
        rep: s.reps,
        distance: s.distance,
        interval: s.minutes * 60 + s.seconds,
        energy_zone: s.energy_zone,
        description: s.description,
      }));

      const result = await postRepo.sharePracticeFeed(
        practiceData,
        practiceImageData,
        lapsData,
        text || "",
        postPhoto || "",
        postVideo || "",
        userId,
        coachUserId
      );

      if (result.status === 1) {
        toast.success("Practice posted to feed successfully");
      } else {
        toast.error(result.msg || "Failed to post practice to feed");
      }

      setIsAddPostDialogOpen(false);
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong");
    } finally {
      setLoadingAction(false);
    }
  };

  const [isFavoriteActionDialogOpen, setIsFavoriteActionDialogOpen] = useState(false);
  const [isFavoriteFormDialogOpen, setIsFavoriteFormDialogOpen] = useState(false);

  const handleTwoDigitNumber = (
        value: string,
        setter: React.Dispatch<React.SetStateAction<string>>
      ) => {
        const digitsOnly = value.replace(/\D/g, "");
        let twoDigits = digitsOnly.slice(0, 2);

        if (twoDigits === "") {
          setter("");
          return;
        }

        const num = Number(twoDigits);

        if (num > 59) {
          twoDigits = "59";
        }

        setter(twoDigits);
      };


  const [isFromFavoriteTemplate, setIsFromFavoriteTemplate] = useState(false);


  return (
  <div className="space-y-6">
        {loadingAction && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-900 px-6 py-4 rounded-lg shadow-lg text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-3"></div>
        <p className="text-sm text-muted-foreground">Processing...</p>
        </div>
        </div>
        )}

        {loadingAction && (
          <div className="fixed inset-0 z-40 bg-transparent pointer-events-auto"></div>
        )}

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            {/* <h1 className="text-3xl mb-2">Training Sets</h1> */}
            <h1 className="text-3xl mb-2">
              {practiceId ? "Training Sets" : "Create a Set"}
            </h1>

            <p className="text-muted-foreground">
              Build and customize your workout sets
            </p>
          </div>
          {!noLaps && sets.length > 0 && (
            <Badge variant="secondary" className="px-3 py-1 flex items-center">
              <Timer className="mr-1 h-4 w-4" />
              Total: {calculateTotalTime()}
            </Badge>
          )}
        </div>

        {/* Top-Right Icon Actions */}
        <TooltipProvider>
          <div className="flex justify-end gap-3 mt-2">

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon"
                  className="bg-green-600 hover:bg-green-700 text-white"
                  onClick={() => setIsAddSetDialogOpen(true)}
                  disabled={loadingAction}
                >
                  <Plus className="h-5 w-5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Add Set</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon"
                  
                    className="bg-orange-600 hover:bg-orange-700 text-white"
                  onClick={() => setIsLadderDialogOpen(true)}
                  disabled={loadingAction}
                >
                  <TrendingUp className="h-5 w-5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Add Ladder</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon"
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                  onClick={() => setIsShareDialogOpen(true)}
                  disabled={loadingAction}
                >
                  <Share2 className="h-5 w-5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Share</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon"
                  className="bg-red-600 hover:bg-red-700 text-white"
                  onClick={() => setIsFavoriteActionDialogOpen(true)}
                  disabled={loadingAction}
                >
                  <Heart className="h-5 w-5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Favorite</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  onClick={() => onSectionChange?.("practiceclock", { sets, practiceId, name })}
                  disabled={loadingAction}
                >
                  <Timer className="h-5 w-5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Start Practice</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon"
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={handleSaveLaps}
                  disabled={loadingAction}
                >
                  <Save className="h-5 w-5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Save</TooltipContent>
            </Tooltip>

          </div>
        </TooltipProvider>

        <div className="grid gap-6">
          {/* Main Sets Area */}
          <div className="lg:col-span-3">
            <SetList
              sets={sets}
              energyZones={energyZones}
              onUpdate={updateSet}
              onDelete={handleConfirmDelete}
              calculateTotalTime={calculateTotalTime}
              calculateTotalDistance={calculateTotalDistance}
            />
          </div>
        </div>

        <div id="practice-print-area" className="hidden print:block">

          <h1 className="text-2xl font-bold mb-4">Practice Summary</h1>

          <p><strong>Name:</strong> {name}</p>
          <p><strong>Description:</strong> {description}</p>

          <h2 className="text-xl font-semibold mt-4 mb-3">Practice Sets</h2>

          <table className="w-full border-collapse print-table">
            <thead>
              <tr>
                <th className="border px-3 py-2 text-left">Set</th>
                <th className="border px-3 py-2 text-left">Reps</th>
                <th className="border px-3 py-2 text-left">Distance</th>
                <th className="border px-3 py-2 text-left">Interval</th>
                <th className="border px-3 py-2 text-left">Energy Zone</th>
                <th className="border px-3 py-2 text-left">Description</th>
              </tr>
            </thead>
            <tbody>
              {sets.map((s, i) => (
                <tr key={i}>
                  <td className="border px-3 py-2">Set {s.number}</td>
                  <td className="border px-3 py-2">{s.reps}</td>
                  <td className="border px-3 py-2">{s.distance}</td>
                  <td className="border px-3 py-2">
                    {s.minutes}:{s.seconds.toString().padStart(2, "0")}
                  </td>
                  <td className="border px-3 py-2">{s.energy_zone}</td>
                  <td className="border px-3 py-2">{s.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <DeleteSetDialog
          isOpen={isDeleteDialogOpen}
          onOpenChange={setIsDeleteDialogOpen}
          onConfirm={removeSet}
        />

        <PracticeClockDialog
          isOpen={isClockViewOpen}
          onOpenChange={setIsClockViewOpen}
          timer={timer}
          isRunning={isRunning}
          isPaused={isPaused}
          isStarting={isStarting}
          totalDistance={calculateTotalDistance()}
          totalSets={sets.length}
          onStart={handleStartPractice}
          onPause={handlePausePractice}
          onResume={handleResumePractice}
          onReset={handleResetPractice}
          onFinish={handleFinishPractice}
        />

        <Dialog open={isFavoriteActionDialogOpen} onOpenChange={setIsFavoriteActionDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Favorite Sets</DialogTitle>
              {/* <DialogDescription>Select how you want to proceed.</DialogDescription> */}
            </DialogHeader>

            <div className="flex flex-col space-y-3 mt-4">
                  <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => {
                        setIsFavoriteActionDialogOpen(false);
                        loadAllPractices();
                      }}
                    >
                      Open
                    </Button>

              <Button
                onClick={() => {
                  setIsFavoriteActionDialogOpen(false);
                  setIsFavoriteFormDialogOpen(true);
                }}
              variant="outline"
                className="w-full"
              >
                Save
            </Button>

              <Button
                onClick={() => {
                  const selectedPractice = JSON.parse(
                    localStorage.getItem("selected_practice") || "{}"
                  );
                  
                  // Use existing draft ID if this is an update, otherwise create new
                  const draftId = selectedPractice.isDraft 
                    ? selectedPractice.id 
                    : `draft-${Date.now()}`;

                  const toNumber = (v: number | "") => (typeof v === "number" ? v : 0);

                  const draftData = {
                    id: draftId,
                    name: name || "Untitled Draft",
                    description: description || "",
                    date: new Date().toLocaleDateString("en-US", {
                      month: "2-digit",
                      day: "2-digit",
                      year: "numeric",
                    }),
                    type: "draft" as const,
                    yardage: sets.reduce(
                      (sum, set) => sum + toNumber(set.reps) * toNumber(set.distance),
                      0
                    ),
                    practice_time: "",
                    sets,
                    createdAt: new Date().toISOString(),
                  };


                  const existingDrafts = JSON.parse(
                    localStorage.getItem("practice_drafts") || "[]"
                  );
                  
                  // Remove old draft if updating existing one
                  const filteredDrafts = existingDrafts.filter(
                    (d: any) => d.id !== draftId
                  );
                  
                  const updatedDrafts = [...filteredDrafts, draftData];
                  localStorage.setItem("practice_drafts", JSON.stringify(updatedDrafts));
                  
                  setIsFavoriteActionDialogOpen(false);
                  toast.success("Draft saved");
                  
                  // Update selected practice to mark as draft
                  localStorage.setItem(
                    "selected_practice",
                    JSON.stringify({
                      ...selectedPractice,
                      isDraft: true,
                      sets: sets,
                    })
                  );
                }}
                variant="outline"
                className="w-full"
              >
                Save as Draft
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={isFavoriteFormDialogOpen} onOpenChange={setIsFavoriteFormDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Save as Favorite</DialogTitle>
              <DialogDescription>Update the name and description before saving.</DialogDescription>
            </DialogHeader>

            <div className="space-y-4 mt-4">

              <div>
                <label className="block text-sm mb-1">Name</label>
                <input
                  className="w-full border rounded p-2"
                  value={newPracticeName}
                  onChange={(e) => setNewPracticeName(e.target.value)}
                />
              </div>

              <div>
                <label className="block text-sm mb-1">Description</label>
                <textarea
                  className="w-full border rounded p-2"
                  value={newPracticeDescription}
                  onChange={(e) => setNewPracticeDescription(e.target.value)}
                />
              </div>

              <Button
                className="w-full bg-green-600 hover:bg-green-700 text-white"
                onClick={async () => {
                  if (!ensureSetsExist()) return;
                      setIsFavoriteFormDialogOpen(false);
                      setLoadingAction(true);
                            
                      try {
                        if (!localPracticeId) {
                          setPendingFavorite(true);
                          setIsCreatingFromFavorite(true);
                          setIsNewPracticeDialogOpen(true);
                          return;
                        }

                        await savePracticeAndLaps(
                          localPracticeId,
                          newPracticeName || name,
                          newPracticeDescription || description,
                          true
                        );

                        setIsFavorite(true);
                        toast.success("Practice saved as favorite");
                        onSectionChange?.("practice");
                      } catch {
                        toast.error("Failed to save favorite");
                      } finally {
                        setLoadingAction(false);
                      }
                    }}>

                Save Favorite
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={isConnectDialogOpen} onOpenChange={setIsConnectDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Clock Not Connected</DialogTitle>
              <DialogDescription>
                Please connect your Swimnerd Clock first using the{" "}
                <b>Tools</b> page before toggling power.
              </DialogDescription>
            </DialogHeader>
            <div className="flex justify-end mt-4">
              <Button onClick={() => setIsConnectDialogOpen(false)} variant="default">
                OK
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={isShareDialogOpen} onOpenChange={setIsShareDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Share Practice</DialogTitle>
              <DialogDescription>Select how you want to share this practice.</DialogDescription>
            </DialogHeader>

            <div className="flex flex-col space-y-3 mt-4">

              <Button
          variant="default"
          onClick={async () => {
            window.print();
          }}
        >
          Print Practice
        </Button>

              <Button
          variant="secondary"
          onClick={async () => {
            const pdfUrl = await generatePracticePDF();
            if (!pdfUrl) return;

            const subject = encodeURIComponent(name || "Training Practice");
            const from = encodeURIComponent(
              secureStorage.get("current_user_email") || ""
            );

            const body = encodeURIComponent(
              `Your practice PDF is attached as a link:\n\n${pdfUrl}\n\nDownload and attach manually if needed.`
            );

            window.location.href =
              `https://mail.google.com/mail/?view=cm&fs=1&from=${from}&su=${subject}&body=${body}`;
          }}
        >
          Email Practice
        </Button>

        <Button
        variant="outline"
        onClick={() => {
        setIsShareDialogOpen(false);
        setIsAddPostDialogOpen(true);
        }}
        >
        Share to Feed
        </Button>

            
            </div>
          </DialogContent>
        </Dialog>

        <Dialog
          open={isLadderDialogOpen}
          onOpenChange={(open) => {
            setIsLadderDialogOpen(open);
            if (!open) resetLadderForm();
          }}
        >

          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Ladder</DialogTitle>
            </DialogHeader>

              {ladderFormError && (
                <p className="text-sm text-red-600 font-medium">
                  {ladderFormError}
                </p>
              )}


            <div className="space-y-4 mt-4">

              <div>
                <label className="block text-sm">Rounds</label>
                <input
                  type="number"
                  className="w-full border p-2 rounded"
                  value={ladderRounds}
                  onChange={(e) => setLadderRounds(e.target.value)}
                />
              </div>

              <div>
                <label className="block text-sm">Reps (Max: {MAX_REPS})</label>
                <input
                  type="number"
                  className="w-full border p-2 rounded"
                  value={ladderReps}
                  min="1"
                  max={MAX_REPS}
                  onChange={(e) => {
                    const value = e.target.value;
                    const num = parseInt(value) || 0;
                    if (value === '' || (num >= 1 && num <= MAX_REPS)) {
                      setLadderReps(value);
                    } else if (num > MAX_REPS) {
                      setLadderReps(MAX_REPS.toString());
                      toast.error(`Reps cannot exceed ${MAX_REPS}`);
                    }
                  }}
                />
              </div>

              <div>
                <label className="block text-sm">Distance (Max: {MAX_DISTANCE}m)</label>
                <input
                  type="number"
                  className="w-full border p-2 rounded"
                  value={ladderDistance}
                  min="1"
                  max={MAX_DISTANCE}
                  onChange={(e) => {
                    const value = e.target.value;
                    const num = parseInt(value) || 0;
                    if (value === '' || (num >= 1 && num <= MAX_DISTANCE)) {
                      setLadderDistance(value);
                    } else if (num > MAX_DISTANCE) {
                      setLadderDistance(MAX_DISTANCE.toString());
                      toast.error(`Distance cannot exceed ${MAX_DISTANCE} meters`);
                    }
                  }}
                />
              </div>

              <div className="flex gap-2">
                <div className="flex-1">
                  <label className="block text-sm">Minutes</label>
                  <input
                      type="text"
                      inputMode="numeric"
                      maxLength={2}
                      className="w-full border p-2 rounded"
                      value={ladderMinutes}
                      onChange={(e) =>
                        handleTwoDigitNumber(e.target.value, setLadderMinutes)
                      }
                    />

                </div>

                <div className="flex-1">
                  <label className="block text-sm">Seconds</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={2}
                    className="w-full border p-2 rounded"
                    value={ladderSeconds}
                    onChange={(e) =>
                      handleTwoDigitNumber(e.target.value, setLadderSeconds)
                    }
                  />

                </div>
              </div>

              <div>
                <label className="block text-sm">Energy Zone</label>
                <select
                  className="w-full border p-2 rounded"
                  value={ladderEnergy}
                  onChange={(e) => setLadderEnergy(e.target.value)}
                >
                  {energyZones.map((zone) => (
                    <option key={zone} value={zone}>{zone}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm">Description</label>
                <input
                  className="w-full border p-2 rounded"
                  value={ladderDescription}
                  onChange={(e) => setLadderDescription(e.target.value)}
                />
              </div>

              <Button
                className="w-full bg-blue-600 text-white"
                onClick={() => {
                  const hasError = hasNegativeValue(
                    ladderRounds,
                    ladderReps,
                    ladderDistance,
                    ladderMinutes,
                    ladderSeconds
                  );

                  if (hasError) {
                    setLadderFormError("Negative values are not allowed");
                    return;
                  }

                  setLadderFormError(null);
                  createLadderSets();
                  resetLadderForm();
                  setIsLadderDialogOpen(false);
                }}
              >
                Add Ladder
              </Button>

            </div>
          </DialogContent>
        </Dialog>

        <Dialog
            open={isAddSetDialogOpen}
            onOpenChange={(open) => {
              setIsAddSetDialogOpen(open);
              if (!open) resetSingleSetForm();
            }}
          >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Set</DialogTitle>
            </DialogHeader>


            {singleFormError && (
              <p className="text-sm text-red-600 font-medium">
                {singleFormError}
              </p>
            )}

            <div className="space-y-4 mt-4">
              <div>
                <label className="block text-sm">Rounds</label>
                <input
                  type="number"
                  className="w-full border p-2 rounded"
                  value={singleRounds}
                  min="1"
                  onChange={(e) => setSingleRounds(e.target.value)}
                />
              </div>

              <div>
                <label className="block text-sm">Reps (Max: {MAX_REPS})</label>
                <input
                  type="number"
                  className="w-full border p-2 rounded"
                  value={singleReps}
                  min="1"
                  max={MAX_REPS}
                  onChange={(e) => {
                    const value = e.target.value;
                    const num = parseInt(value) || 0;
                    if (value === '' || (num >= 1 && num <= MAX_REPS)) {
                      setSingleReps(value);
                    } else if (num > MAX_REPS) {
                      setSingleReps(MAX_REPS.toString());
                      toast.error(`Reps cannot exceed ${MAX_REPS}`);
                    }
                  }}
                />
              </div>

              <div>
                <label className="block text-sm">Distance (Max: {MAX_DISTANCE}m)</label>
                <input
                  type="number"
                  className="w-full border p-2 rounded"
                  value={singleDistance}
                  min="1"
                  max={MAX_DISTANCE}
                  onChange={(e) => {
                    const value = e.target.value;
                    const num = parseInt(value) || 0;
                    if (value === '' || (num >= 1 && num <= MAX_DISTANCE)) {
                      setSingleDistance(value);
                    } else if (num > MAX_DISTANCE) {
                      setSingleDistance(MAX_DISTANCE.toString());
                      toast.error(`Distance cannot exceed ${MAX_DISTANCE} meters`);
                    }
                  }}
                />
              </div>

              <div className="flex gap-2">
                <div className="flex-1">
                  <label className="block text-sm">Minutes</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={2}
                    className="w-full border p-2 rounded"
                    value={singleMinutes}
                    onChange={(e) =>
                      handleTwoDigitNumber(e.target.value, setSingleMinutes)
                    }
                  />

                </div>

                <div className="flex-1">
                  <label className="block text-sm">Seconds</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={2}
                    className="w-full border p-2 rounded"
                    value={singleSeconds}
                    onChange={(e) =>
                      handleTwoDigitNumber(e.target.value, setSingleSeconds)
                    }
                  />

                </div>
              </div>

              <div>
                <label className="block text-sm">Energy Zone</label>
                <select
                  className="w-full border p-2 rounded"
                  value={singleEnergy}
                  onChange={(e) => setSingleEnergy(e.target.value)}
                >
                  {energyZones.map((zone) => (
                    <option key={zone} value={zone}>{zone}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm">Description</label>
                <input
                  className="w-full border p-2 rounded"
                  value={singleDescription}
                  onChange={(e) => setSingleDescription(e.target.value)}
                />
              </div>

              <Button
                className="w-full bg-blue-600 text-white"
                onClick={() => {
                  const hasError = hasNegativeValue(
                    singleRounds,
                    singleReps,
                    singleDistance,
                    singleMinutes,
                    singleSeconds
                  );

                  if (hasError) {
                    setSingleFormError("Negative values are not allowed");
                    return;
                  }

                  setSingleFormError(null);
                  createSingleSet();
                  setIsAddSetDialogOpen(false);
                }}
              >
                Add Set
              </Button>



            </div>
          </DialogContent>
        </Dialog>

        <Dialog
          open={isEmptySetsDialogOpen}
          onOpenChange={setIsEmptySetsDialogOpen}
        >
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>No Sets Added</DialogTitle>
              <DialogDescription>
                Please add at least one set or ladder before saving the practice.
              </DialogDescription>
            </DialogHeader>

            <div className="flex justify-end mt-4">
              <Button onClick={() => setIsEmptySetsDialogOpen(false)}>
                OK
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <CreatePracticeDialog
            isOpen={isNewPracticeDialogOpen}
            onOpenChange={setIsNewPracticeDialogOpen}
            practiceName={newPracticeName}
            practiceDescription={newPracticeDescription}
            onNameChange={setNewPracticeName}
            onDescriptionChange={setNewPracticeDescription}
            onSave={async () => {

              // Guard: must have sets
              if (!sets || sets.length === 0) {
                setIsEmptySetsDialogOpen(true);
                return;
              }

              setLoadingAction(true);

              try {
                  const newPracticeId = Math.floor(10000 + Math.random() * 90000);
                  const resolvedFolderId = resolveFolderId();

                  await savePracticeAndLaps(
                    newPracticeId,
                    newPracticeName,
                    newPracticeDescription,
                    pendingFavorite ?? isFavorite,
                    resolvedFolderId
                  );

                  setLocalPracticeId(newPracticeId);

                if (pendingFavorite !== null) {
                  setIsFavorite(pendingFavorite);
                  setPendingFavorite(null);
                }

                // Close dialog FIRST
                setIsNewPracticeDialogOpen(false);

                // Then redirect
                onSectionChange?.("practice");

                if (!isCreatingFromFavorite) {
                  onClose?.();
                }

                setIsCreatingFromFavorite(false);


                toast.success("Practice created successfully");

              } catch (err) {
                console.error(err);
                toast.error("Failed to create practice");
              } finally {
                setLoadingAction(false);
              }
            }}
          />

          <Dialog
          open={isOpenPracticeDialogOpen}
          onOpenChange={setIsOpenPracticeDialogOpen}
        >
  <DialogContent className="max-w-2xl">
    <DialogHeader>
      <DialogTitle>Select Practice</DialogTitle>
      <DialogDescription>
        Choose a practice to open
      </DialogDescription>
    </DialogHeader>

    {loadingPractices ? (
      <p className="text-sm text-muted-foreground">Loading practices...</p>
    ) : allPractices.length === 0 ? (
      <p className="text-sm text-muted-foreground">No practices found</p>
    ) : (
      <div className="space-y-3 max-h-[400px] overflow-y-auto">
        {allPractices.map((practice) => (
          <div  
            key={practice.id}
            className="border rounded-lg p-3 cursor-pointer hover:bg-muted"
           
            onClick={() => {
                const targetIds = [
                  (practice as any).logicalPracticeId,
                  (practice as any).practice_id,
                  (practice as any).folder_practice_model_id,
                  (practice as any).practiceId,
                  practice.practiceId,
                  practice.id,
                ]
                  .filter(Boolean)
                  .map(String);

  const practiceLaps = allPracticeLaps.filter((lap) => {
    const lapIds = [
      lap.practice_id,
      (lap as any).practiceId,
      (lap as any).folder_practice_model_id,
    ]
      .filter(Boolean)
      .map(String);

    return lapIds.some((id) => targetIds.includes(id));
  });

  // Append laps each time a favorite is chosen
  setSets(prev => {
  const offset = prev.length;
  const newSets = practiceLaps.map((lap, index) => {
    const parsedDistance = parseInt(String(lap.distance));
    return {
      id: crypto.randomUUID(),
      number: offset + index + 1,
      reps: parseInt(String(lap.rep)) || 1,
      minutes: Math.floor(
        (parseInt(String(lap.interval_lap ?? (lap as any).interval ?? 0)) || 0) / 60
      ),
      seconds:
        (parseInt(String(lap.interval_lap ?? (lap as any).interval ?? 0)) || 0) % 60,
      distance: (!isNaN(parsedDistance) && lap.distance != null && lap.distance !== '') ? parsedDistance : 25,
      energy_zone: lap.energy_zone || "Warm Up",
      description: lap.description || `Lap ${offset + index + 1}`,
    };
  });

  const updatedSets = normalizeSets([...prev, ...newSets]);
  
  // Mark as manually modified to prevent useEffect from overwriting
  isManuallyModifiedRef.current = true;
  
  // Update localStorage if this is a draft practice
  const selectedPractice = JSON.parse(localStorage.getItem("selected_practice") || "{}");
  if (selectedPractice.isDraft) {
    const updatedPractice = {
      ...selectedPractice,
      sets: updatedSets,
    };
    localStorage.setItem("selected_practice", JSON.stringify(updatedPractice));
  }
  
  return updatedSets;
});

setIsDirtySets(true);


if (!practiceId) {
  setLocalPracticeId(undefined);
  setIsFromFavoriteTemplate(true);
} else {
  setLocalPracticeId(practiceId);
  setIsFromFavoriteTemplate(false);
}


  onSectionChange?.("sets", {
    name: practice.name,
    description: practice.description,
  });

  setIsOpenPracticeDialogOpen(false);
}}



          >
            <p className="font-medium">{practice.name}</p>
            <p className="text-sm text-muted-foreground">
              {practice.description || "No description"}
            </p>
          </div>
        ))}
      </div>
    )}
  </DialogContent>
</Dialog>


        <AddPostDialog
          open={isAddPostDialogOpen}
          onOpenChange={setIsAddPostDialogOpen}
          onSubmit={handleSubmitPracticeFeedPost}
        />
    </div>
  );
}
